import React, { useEffect, useMemo, useState } from "react";
import { CircleAlert, TriangleAlert } from "lucide-react";
import LoadingShimmer from "./LoadingShimmer";
import { WeatherIcon } from "./WeatherIcon";
import { resolveWeatherPresentation, WEATHER_FAMILIES } from "../lib/weatherPresentation";
import ScoreExplanation from "./ScoreExplanation";
import CautionIndicator from "./CautionIndicator";
import HazardSourceNote from "./HazardSourceNote";
import { getSiteAvailability } from "../config/availability";
import { HAZARDS_V1 } from "../config/hazards";
import { getPrecipitationLabel } from "../utils/precipitation";
import { cautionLevelLabel } from "../lib/cautionLabel";
import { getWarningTypeLabel } from "./routePlannerDetailsHelpers";

import {
  convertTemp,
  convertRain,
  convertWind,
  convertDistanceKm,
  formatNumber,
  TEMP_UNIT_LABEL,
  RAIN_UNIT_LABEL,
  WIND_UNIT_LABEL,
  DIST_UNIT_LABEL,
} from "../lib/scoring";

// --- Hazard thresholds (keep in sync with relocationEngine normalizeConfig.hazards) ---

function computeWarningsFromRow(r) {
  const out = [];

  const wind = typeof r?.windMax === "number" ? r.windMax : null;
  const gust = typeof r?.windGust === "number" ? r.windGust : null;
  const rain = typeof r?.rain === "number" ? r.rain : null;
  const tmin = typeof r?.tmin === "number" ? r.tmin : null;
  const tmax = typeof r?.tmax === "number" ? r.tmax : null;

  // wind
  if (wind != null) {
    if (wind >= HAZARDS_V1.windHigh) out.push({ type: "wind", level: "high", value: wind });
    else if (wind >= HAZARDS_V1.windWarn) out.push({ type: "wind", level: "warn", value: wind });
  }

  // gust
  if (gust != null) {
    if (gust >= HAZARDS_V1.gustHigh) out.push({ type: "gust", level: "high", value: gust });
    else if (gust >= HAZARDS_V1.gustWarn) out.push({ type: "gust", level: "warn", value: gust });
  }

  // rain (daily)
  if (rain != null) {
    if (rain >= HAZARDS_V1.rainHigh) out.push({ type: "rain", level: "high", value: rain });
    else if (rain >= HAZARDS_V1.rainWarn) out.push({ type: "rain", level: "warn", value: rain });
  }

  // temp (use tmin for cold risk, tmax for heat risk)
  if (tmin != null) {
    if (tmin <= HAZARDS_V1.tempLowHigh) out.push({ type: "tempLow", level: "high", value: tmin });
    else if (tmin <= HAZARDS_V1.tempLowWarn)
      out.push({ type: "tempLow", level: "warn", value: tmin });
  }

  if (tmax != null) {
    if (tmax >= HAZARDS_V1.tempHighHigh) out.push({ type: "tempHigh", level: "high", value: tmax });
    else if (tmax >= HAZARDS_V1.tempHighWarn)
      out.push({ type: "tempHigh", level: "warn", value: tmax });
  }

  return out;
}

function rowHasHighWarning(warnings) {
  return Array.isArray(warnings) && warnings.some((w) => w?.level === "high");
}

function rowHasWarning(warnings) {
  return Array.isArray(warnings) && warnings.some((w) => w?.level === "warn");
}

// Ticket #436 F1/D3: same warnings array that decided hasHigh/hasWarn above
// — label and reasons can never disagree. High wins exactly as before;
// reasons are the unique warning TYPE labels at that same level, never raw
// values/units, never a fabricated cause when none exist.
function rowCautionLevelAndReasons(warnings) {
  if (!Array.isArray(warnings) || warnings.length === 0) return { level: null, types: [] };
  const level = warnings.some((w) => w?.level === "high")
    ? "high"
    : warnings.some((w) => w?.level === "warn")
      ? "warn"
      : null;
  if (!level) return { level: null, types: [] };
  const types = [...new Set(warnings.filter((w) => w?.level === level).map((w) => w.type))];
  return { level, types };
}

export default function ForecastTable({
  site,
  userLoc,
  distanceToKm,
  rows,
  loading,
  error,
  units,
  mapSlot,
  lang,
  t,
  onSelectDay,
}) {
  const totalPoints = useMemo(() => rows.reduce((s, r) => s + (r.points ?? 0), 0), [rows]);

  const availability = useMemo(() => {
    if (!site?.id) return null;
    return getSiteAvailability(site.id, new Date());
  }, [site?.id]);

  const availabilityBadge = useMemo(() => {
    if (!availability) return null;

    // --- Seasonal closed (winter) ---
    // We don't know exact open day -> show generic "Most open in May"
    if (availability.isClosed && availability.mode === "seasonal") {
      const text = lang === "is" ? "🔒 Lokað" : "🔒 Closed";
      const title =
        typeof t === "function"
          ? t("availabilityMostOpenInMay")
          : lang === "is"
            ? "Flest tjaldsvæði opna í maí."
            : "Most campsites open in May.";

      return {
        text,
        title,
      };
    }

    // --- All-year limited service info ---
    if (availability.mode === "allYear" && availability.winterStatus === "year_round_limited") {
      const text = lang === "is" ? "ℹ️ Opið (takmörkuð þjónusta)" : "ℹ️ Open (limited service)";
      return { text, title: availability.winterNotes || "" };
    }

    // --- All-year full ---
    // Keep it clean: show a positive badge only if you want it.
    // If you want less noise, return null here.
    if (availability.mode === "allYear" && availability.winterStatus === "year_round_full") {
      return null;
    }

    return null;
  }, [availability, lang, t]);

  // ✅ Pro hazard animation (runs briefly, then stops)
  const [animateHighHazards, setAnimateHighHazards] = useState(true);

  useEffect(() => {
    setAnimateHighHazards(true);
    const tt = setTimeout(() => setAnimateHighHazards(false), 6000);
    return () => clearTimeout(tt);
  }, [site?.id]);

  // Ticket #436 F2: the source note shows once inside the opened details
  // panel, only when at least one DISPLAYED row actually has a caution —
  // never a floating note while loading/error/collapsed/no indicators.
  const anyRowHasCaution = useMemo(
    () => Array.isArray(rows) && rows.some((r) => computeWarningsFromRow(r).length > 0),
    [rows]
  );

  // UX Miði #376 — supporting-detail disclosure. Presentation-only: every hook
  // above this line runs unconditionally, same as before this change, so
  // data/fetch timing is untouched (rows are shared scoring input from
  // App.jsx's useForecast, computed regardless of this component's UI state).
  const [detailsOpen, setDetailsOpen] = useState(false);

  const header = (
    <div className="flex items-baseline justify-between mb-3">
      <h2 className="text-lg font-semibold">
        <span className="inline-flex items-center gap-2">
          <span>{site?.name || "—"}</span>

          {availabilityBadge ? (
            <span
              title={availabilityBadge.title}
              className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold
                 border border-slate-200 dark:border-slate-700
                 bg-white/70 dark:bg-slate-900/60 text-slate-700 dark:text-slate-200"
            >
              {availabilityBadge.text}
            </span>
          ) : null}
        </span>

        {userLoc && site && distanceToKm != null && (
          <span className="ml-2 text-sm text-slate-500 dark:text-slate-300">
            · {formatNumber(convertDistanceKm(distanceToKm, units), 1)} {DIST_UNIT_LABEL[units]}{" "}
            {t?.("away")}
          </span>
        )}
      </h2>

      <div className="text-sm text-slate-600 dark:text-slate-300">
        {site?.lat?.toFixed?.(4)}, {site?.lon?.toFixed?.(4)}
      </div>
    </div>
  );

  if (!detailsOpen) {
    return (
      <div className="card rounded-2xl shadow-sm border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-700 p-4">
        {header}

        <button
          type="button"
          onClick={() => setDetailsOpen(true)}
          aria-expanded={false}
          aria-controls="forecast-details-panel"
          className="w-full flex items-center justify-between gap-2 rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2.5 text-sm font-semibold text-sky-700 dark:text-sky-300 hover:bg-sky-50/60 dark:hover:bg-slate-800/60"
        >
          <span className="inline-flex items-center gap-2">
            <span aria-hidden>🌤️</span>
            {t?.("forecastShowDetailsCta")}
          </span>
          <span aria-hidden>→</span>
        </button>
      </div>
    );
  }

  return (
    <div className="card rounded-2xl shadow-sm border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-700 p-4">
      {header}

      <div className="mb-3 flex justify-end">
        <button
          type="button"
          onClick={() => setDetailsOpen(false)}
          aria-expanded={true}
          aria-controls="forecast-details-panel"
          className="text-xs font-semibold underline decoration-dotted underline-offset-2 opacity-75 hover:opacity-100"
        >
          {t?.("forecastHideDetailsCta")}
        </button>
      </div>

      <div id="forecast-details-panel">
      {/* Total */}
      <div className="mb-3 text-sm">
        <span className="inline-flex items-center rounded-full bg-white/80 dark:bg-slate-900/70 glass px-3 py-1 shadow-sm border border-slate-200 dark:border-slate-600">
          {t?.("total")} (7 {t?.("days")}):
          <span className="ml-2 font-semibold">
            {totalPoints} {t?.("pts")}
          </span>
        </span>
        {availability?.isClosed && availability?.mode === "seasonal" && (
          <div className="mb-4 text-xs text-slate-500 dark:text-slate-400">
            <span className="inline-flex items-start gap-2">
              <span className="mt-0.5">ℹ️</span>
              <span className="max-w-[700px]">{t?.("seasonClosedInfo")}</span>
            </span>
          </div>
        )}
      </div>

      {loading && <LoadingShimmer rows={8} />}
      {error && (
        <div className="py-10 text-center text-red-600">
          {t?.("forecastLoadFailed") ?? "Could not load forecast."}
        </div>
      )}

      {!loading && !error && (
        <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm table-sticky">
              <thead className="bg-slate-50 dark:bg-slate-900/80">
                <tr className="border-b border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-200">
                  <th className="py-3 pl-4 pr-3 font-semibold"></th>
                  <th className="py-3 pr-3 font-semibold">{t?.("weather")}</th>
                  <th className="py-3 pr-3 font-semibold">{t?.("day")}</th>
                  <th className="py-3 pr-3 font-semibold">
                    <span className="sm:hidden">{t?.("tempMinShort")}</span>
                    <span className="hidden sm:inline">{t?.("tempMin")}</span>
                  </th>

                  <th className="py-3 pr-3 font-semibold">
                    <span className="sm:hidden">{t?.("tempMaxShort")}</span>
                    <span className="hidden sm:inline">{t?.("tempMax")}</span>
                  </th>

                  <th className="py-3 pr-3 font-semibold">
                    <span className="sm:hidden">{t?.("windMaxShort")}</span>
                    <span className="hidden sm:inline">{t?.("windMax")}</span>
                  </th>

                  <th className="py-3 pr-3 font-semibold">
                    <span className="sm:hidden">{t?.("rainShort")}</span>
                    <span className="hidden sm:inline">{t?.("rain")}</span>
                  </th>
                  <th className="py-3 pr-4 font-semibold sr-only">Open</th>
                </tr>
              </thead>

              <tbody className="[&>tr:nth-child(even)]:bg-slate-50 dark:[&>tr:nth-child(even)]:bg-slate-800/40">
                {rows.map((r, idx) => {
                  // Null-preserving: a missing/unusable summaryCode or code
                  // must reach the resolver as unknown, never coerced to
                  // WMO 0 (clear sky) — see approved prompt Round 2 §1.
                  const displayCode = r.summaryCode ?? r.code ?? null;
                  const presentation = resolveWeatherPresentation(displayCode, { isDay: true });
                  // Ticket 402 (#402): prefer the richer temporal narrative
                  // key when the summarizer detected one (e.g. "Rain early,
                  // dry later") — it already describes the day more fully
                  // than the bare WMO-family text, and the icon above still
                  // comes from the same coherent `displayCode`.
                  const weatherKey = r.summaryTextKey ?? presentation.textKey;

                  const warnings = computeWarningsFromRow(r);
                  const hasHigh = rowHasHighWarning(warnings);
                  const hasWarn = rowHasWarning(warnings);
                  // Ticket #436 D3: same `warnings` array hasHigh/hasWarn
                  // already used — label and reasons can never disagree.
                  const { level: cautionLevel, types: cautionTypes } =
                    rowCautionLevelAndReasons(warnings);
                  const cautionReasonLabels = cautionTypes.map((ty) => getWarningTypeLabel(ty, t));
                  // D5: the prefix below is byte-identical to the original
                  // for a row with no caution (empty suffix) — existing
                  // tests match /^day Day \d/ and /^day/i against it.
                  const ariaLabelSuffix = cautionLevel
                    ? ` · ${cautionLevelLabel(cautionLevel, t)}${
                        cautionReasonLabels.length ? `: ${cautionReasonLabels.join(", ")}` : ""
                      }`
                    : "";

                  return (
                    <tr
                      key={r.date}
                      onClick={() => onSelectDay?.(r, idx)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onSelectDay?.(r, idx);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label={`${t?.("day") ?? "Day"} ${r.dayLabel}${ariaLabelSuffix}`}
                      className={`
                        border-b last:border-0 border-slate-100 dark:border-slate-800
                        hover:bg-sky-50/50 dark:hover:bg-slate-800/60
                        cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-400/60
                      `}
                    >
                      {/* Score */}
                      <td className="relative py-2 pl-4 pr-3">
                        {hasHigh && (
                          <>
                            {/* Left severity bar */}
                            <span
                              aria-hidden
                              className="
                                  absolute left-0 top-0 h-full w-1
                                  bg-rose-500/70 dark:bg-rose-400/60
                                "
                            />

                            {/* Soft gradient overlay */}
                            <span
                              aria-hidden
                              className="
                                pointer-events-none
                                absolute -left-4 -right-[999px] top-0 bottom-0
                                bg-gradient-to-r from-rose-500/4 via-rose-500/3 to-transparent
                                dark:from-rose-400/8 dark:via-rose-400/5
                              "
                            />
                          </>
                        )}

                        <span
                          title={`Base ${r.basePts} (Temp ${
                            formatNumber(convertTemp(r.tmax, units)) ?? "?"
                          }${TEMP_UNIT_LABEL[units]}) Wind ${r.windPen} (${
                            formatNumber(convertWind(r.windMax, units)) ?? "?"
                          } ${WIND_UNIT_LABEL[units]}) Rain ${r.rainPen} (${
                            formatNumber(convertRain(r.rain, units)) ?? "?"
                          } ${RAIN_UNIT_LABEL[units]}) = ${r.points} → ${r.class}`}
                          className={
                            "relative z-10 inline-flex flex-col items-center justify-center gap-0.5 rounded-full px-2 py-2 text-[9px] font-semibold cursor-help w-14 h-14 text-center " +
                            (r.class === "Best"
                              ? "bg-green-100 text-green-800"
                              : r.class === "Good"
                                ? "bg-emerald-100 text-emerald-800"
                                : r.class === "Ok"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : r.class === "Fair"
                                    ? "bg-amber-100 text-amber-800"
                                    : "bg-red-100 text-red-800")
                          }
                        >
                          {presentation.isUnknown ? (
                            <span
                              role="img"
                              aria-label={t?.(weatherKey)}
                              className="flex h-9 w-9 items-center justify-center text-slate-400 dark:text-slate-500"
                            >
                              <span aria-hidden>—</span>
                            </span>
                          ) : (
                            <WeatherIcon
                              iconId={presentation.iconId}
                              aria-label={t?.(weatherKey)}
                              className="w-9 h-9"
                              role="img"
                            />
                          )}

                          <span>{t?.(r.class?.toLowerCase?.()) ?? r.class}</span>

                          {(hasHigh || hasWarn) && (
                            <span
                              className={`
                                ring-offset-1 ring-offset-white dark:ring-offset-slate-900
                                absolute -right-[4px] -top-[2px]
                                inline-flex items-center justify-center
                                w-[18px] h-[18px] rounded-full text-[12px] leading-none
                                bg-white/95 dark:bg-slate-50/90
                                shadow-sm
                                ${
                                  hasHigh
                                    ? "ring-2 ring-rose-500 dark:ring-rose-400"
                                    : "ring-1 ring-amber-400/60 dark:ring-amber-400/70"
                                }
                                ${
                                  hasHigh
                                    ? "drop-shadow-[0_0_8px_rgba(244,63,94,0.45)]"
                                    : "drop-shadow-none"
                                }
                                ${hasHigh && animateHighHazards ? "hazard-glow" : ""}
                              `}
                            >
                              {/* #436 F1: decorative — the row's aria-label
                                  and the visible reason line below already
                                  carry the accessible text, never title-only. */}
                              {hasHigh ? (
                                <TriangleAlert aria-hidden="true" size={11} className="text-rose-600 dark:text-rose-500" />
                              ) : (
                                <CircleAlert aria-hidden="true" size={11} className="text-amber-600 dark:text-amber-500" />
                              )}
                            </span>
                          )}
                        </span>
                      </td>

                      {/* Weather text */}
                      <td className="py-2 pr-3 text-slate-700 dark:text-slate-200">
                        {(() => {
                          const base = t?.(weatherKey);

                          // Ticket 402 (#402): a temporal narrative already
                          // tells the fuller, more specific story ("Rain
                          // early, dry later") — the generic amount-based
                          // getPrecipitationLabel caption below must never
                          // overwrite it (approved prompt §3).
                          if (r.summaryTextKey) return base;

                          // Keyed from the same displayed summary family
                          // (summaryCode ?? row.code) that produced the
                          // headline/icon above — never from raw row.code
                          // alone, so the caption can't contradict what's
                          // already shown (approved prompt Round 2 §3).
                          // Freezing precipitation gets truthful base
                          // wording only; it must never borrow rain/snow
                          // duration phrasing.
                          const isSnow = presentation.family === WEATHER_FAMILIES.SNOW;
                          const isRain =
                            presentation.family === WEATHER_FAMILIES.RAIN ||
                            presentation.family === WEATHER_FAMILIES.DRIZZLE;

                          if (!isSnow && !isRain) return base;

                          const label = getPrecipitationLabel(isSnow ? "snow" : "rain", r.rain, t, {
                            precipStartHour: r.precipStartHour,
                            precipDurationHours: r.precipDurationHours,
                            tmin: r.tmin,
                            tmax: r.tmax,
                          });

                          return label || base;
                        })()}

                        {/* Ticket #436 F1/D5: a separate element AFTER the
                            summary IIFE above — summaryTextKey early return
                            and the precipitation caption logic are
                            untouched. Visible short line: severity label
                            plus the available reason TYPE labels, no raw
                            values/units. */}
                        {cautionLevel && (
                          <div className="mt-0.5">
                            <CautionIndicator
                              level={cautionLevel}
                              t={t}
                              reasons={cautionReasonLabels}
                              className="text-[11px]"
                            />
                          </div>
                        )}
                      </td>

                      <td className="py-2 pr-3 whitespace-nowrap font-medium">{r.dayLabel}</td>

                      <td className="py-2 pr-3">
                        {formatNumber(convertTemp(r.tmin, units))} {TEMP_UNIT_LABEL[units]}
                      </td>

                      <td className="py-2 pr-3">
                        {formatNumber(convertTemp(r.tmax, units))} {TEMP_UNIT_LABEL[units]}
                      </td>

                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-2">
                          <span>
                            {formatNumber(convertWind(r.windMax, units))} {WIND_UNIT_LABEL[units]}
                          </span>

                          {r.windGust != null &&
                          Number.isFinite(Number(r.windGust)) &&
                          Number(r.windGust) >= 15 ? (
                            <span
                              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 dark:text-slate-300"
                              title={`${t?.("windGust")}: ${formatNumber(convertWind(r.windGust, units))} ${WIND_UNIT_LABEL[units]}`}
                            >
                              <span aria-hidden>🌬</span>
                              <span className="opacity-80">
                                {formatNumber(convertWind(r.windGust, units))}{" "}
                                {WIND_UNIT_LABEL[units]}
                              </span>
                            </span>
                          ) : null}
                        </div>
                      </td>

                      <td className="py-2 pr-3">
                        {formatNumber(convertRain(r.rain, units))} {RAIN_UNIT_LABEL[units]}
                      </td>

                      <td className="py-2 pr-4 text-right text-slate-400 dark:text-slate-500">
                        <span aria-hidden>›</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Map slot */}
          {mapSlot}
        </div>
      )}

      {/* Ticket #436 F2: once inside the opened panel, only when the
          rendered table actually has at least one row with a caution —
          never while loading/error/no indicators. */}
      {!loading && !error && anyRowHasCaution && (
        <div className="mt-3">
          <HazardSourceNote t={t} />
        </div>
      )}

      {/* Legend */}
      <ScoreExplanation t={t} lang={lang} />
      </div>
    </div>
  );
}
